import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { State } from "../../../redux/slices";
import { useTranslation } from "react-i18next";
import { ThunkDispatch } from "@reduxjs/toolkit";

import {
  fetchQualityCriteria,
  fetchSpeciesTemplate,
  fetchStoragesOfEachLocation,
  fetchStorageTemplate,
} from "../../../redux/actions/GrainWise/grainwiseEditionServices/grainTrackerGetServicesEdition";

import {
  GWComposeState,
  resetAdvancedDeclarationOptionAndDeclarationOptions,
  resetMessagesDone,
  selecteBinIndex,
  setAllotmentPolicies,
  setLocationGrainTracker,
  setPrincipalInfoGT,
  setPrincipalInfoGTCopy,
  setTheBinIsGood,
} from "../../../redux/slices/grainTracker/grainTrackerEdition";
import {
  addNewBin,
  updateBinSaveAndQuite,
} from "../../../redux/actions/GrainWise/grainwiseEditionServices/grainTrackerEdition";

import { GrainTrackerForm } from "./GrainTrackerFormEdition";

import { Alert, Button, FormControl, Tooltip } from "@mui/material";
import { ShowsMessagesSuccess } from "../partsOfForm/GrainTrackerSuccessMessages";
import { ShowsMessagesError } from "../partsOfForm/GrainTrackerErrorMessages";
import GrainTrackerLayoutForm from "../partsOfForm/grainwiseLayout.tsx/GrainWiseStoragesLayout";
import { theBinIsGood } from "../create/GrainTrackerForm";
import { PrincipalInfoGT } from "../../../redux/slices/grainTracker/grainTrackerTypes";
import { SetActionsLayout } from "../../layout/Layout";
import { CheckExpiredToken } from "../../../utils/authentication";
import { resetBinSelection } from "../../../redux/slices/location/singleLocation";
import { ConfirmUnsavedChangesDialog } from "../../common/ConfirmUnsavedChangesDialog";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges";
const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    removeSelectedBin: () => {
      dispatch(resetBinSelection());
    },
    fetchStoragesOfEachLocation: (token: string, orgGeoPointId: number) => {
      dispatch(fetchStoragesOfEachLocation({ token, orgGeoPointId }));
      dispatch(fetchStorageTemplate(token));
      dispatch(fetchSpeciesTemplate(token));
      dispatch(resetMessagesDone());
      dispatch(resetAdvancedDeclarationOptionAndDeclarationOptions());
    },

    updateBin: (navigate: any) => {
      dispatch(updateBinSaveAndQuite({ navigate }));
    },

    setLocationGrainTracker: (graintracker: any) => {
      dispatch(setLocationGrainTracker(graintracker));
    },

    binCreation: (token: string) => {
      dispatch(addNewBin(token));
    },

    selectBinIndex: (index: number) => {
      dispatch(selecteBinIndex(index));
    },

    setTheBinIsGood: (value: boolean) => {
      dispatch(setTheBinIsGood(value));
    },

    setInformationByClicking: (
      binInformation: any,
      principalInfoGT: PrincipalInfoGT,
      principalInfoGTCopy: PrincipalInfoGT,
      token: string,
    ) => {
      if (!binInformation) {
        return;
      }

      dispatch(
        setTheBinIsGood(
          !theBinIsGood(
            binInformation?.cerealTypeId || null,
            binInformation?.storageTemplateId || null,
            binInformation?.totalCapacity?.value || 0,
          ),
        ),
      );

      dispatch(
        setPrincipalInfoGT({
          ...principalInfoGT,
          name: binInformation?.name || "",
          specie: binInformation?.cerealTypeId || null,
          storage: binInformation?.storageTemplateId || null,
          currentCharacteristics: binInformation?.currentCharacteristics || [],
          allotmentPolicies: binInformation?.allotmentPolicies || [],
          tonsCapacity:
            binInformation?.characteristics?.capacityMetricTonsWheatEq || 0,
          id: binInformation?.id || null,
        }),
      );

      dispatch(
        setPrincipalInfoGTCopy({
          ...principalInfoGTCopy,
          name: binInformation?.name || "",
          specie: binInformation?.cerealTypeId || null,
          currentCharacteristics: binInformation?.currentCharacteristics || [],
          storage: binInformation?.storageTemplateId || null,
          allotmentPolicies: binInformation?.allotmentPolicies || [],
          tonsCapacity:
            binInformation?.characteristics?.capacityMetricTonsWheatEq || 0,
          id: binInformation?.id || null,
        }),
      );

      // dispatch(setAllotmentPolicies([]));

      setTimeout(() => {
        dispatch(setAllotmentPolicies(binInformation?.allotmentPolicies || []));
      }, 300);

      if (binInformation?.cerealTypeId) {
        dispatch(
          fetchQualityCriteria({ token, specie: binInformation.cerealTypeId }),
        );
      }
    },
  };
};

export const GrainTrackerStock: FC<any> = () => {
  const [t] = useTranslation("global");
  const { user } = useSelector((state: State) => state.Authentication);
  const navigate = useNavigate();

  const {
    locationGrainTrackerValues,
    listOfStoragesTemplates,
    listOfSpecies,
    locationSelectedIs,
    editBinAllotment,
    editBins,
    deleteAllotmentPolicies,
    principalInfoGT,
    principalInfoGTCopy,
    selectIndexLayoutBin,
    allotmentPolicies,
    qualityCriteria,
    selectedCriteriaToDelete,
    deleteBinSuccess,
    deleteBinError,
  } = useSelector((state: State) => state.GrainWiseEditionReducer);

  const { selectedBin, isBinSelected } = useSelector(GWComposeState);

  const token = user?.token ?? "";

  const {
    fetchStoragesOfEachLocation,
    updateBin,
    setLocationGrainTracker,
    setInformationByClicking,
    binCreation,
    selectBinIndex,
    removeSelectedBin,
  } = SetActions();
  const { logoutClickHandler } = SetActionsLayout();

  const { openConfirmDialog, setOpenConfirmDialog } = useUnsavedChanges({
    currentData: principalInfoGT,
    originalData: principalInfoGTCopy,
  });
  const errorStoragesTemplateEmpty = () => {
    if (listOfStoragesTemplates.storagesTemplates?.data.length === 0) {
      return "Not storages templates";
    } else {
      return "";
    }
  };

  const sumOfErrors = locationGrainTrackerValues?.error?.concat(
    listOfStoragesTemplates?.error?.concat(
      listOfSpecies?.error.concat(errorStoragesTemplateEmpty()),
    ),
  );

  const { isAddingStorage, isEditingBin, storagesList, locationInformation } =
    locationGrainTrackerValues;

  useEffect(() => {
    fetchStoragesOfEachLocation(
      token,
      locationGrainTrackerValues.locationInformation?.id as number,
    );
    CheckExpiredToken(logoutClickHandler, user);
  }, []);

  const managingRedirectUser = () => {
    if (
      JSON.stringify(principalInfoGT) !== JSON.stringify(principalInfoGTCopy)
    ) {
      setOpenConfirmDialog(true);
    } else {
      navigate(`/site/link-users/${locationInformation?.name}`);
    }
  };

  const handleConfirmContinue = () => {
    setOpenConfirmDialog(false);
  };

  return (
    <>
      <div className="steps-container-create-loc">
        <div className="background-graintracker" />
        <div className="step-title">{`${t("Common_options.step")} 2/3`} </div>

        <div className="step-title-create">
          {t("Grain_tracker.storage_title")}
        </div>
        <div className="step-subtitle-grain">
          {t("Grain_tracker.storage_subtitle")}
        </div>
        {sumOfErrors?.length >= 1 ? (
          <div>
            <Alert severity="error">{sumOfErrors}</Alert>
          </div>
        ) : (
          ""
        )}

        <div className="graintracker-form-container mt-3 mb-3">
          <GrainTrackerLayoutForm
            t={t}
            setLocationGrainTracker={setLocationGrainTracker}
            setInformationByClicking={setInformationByClicking}
            binCreation={binCreation}
            selectBinIndex={selectBinIndex}
            locationGrainTrackerValues={locationGrainTrackerValues}
            principalInfoGT={principalInfoGT}
            principalInfoGTCopy={principalInfoGTCopy}
            selectIndexLayoutBin={selectIndexLayoutBin}
            listOfStoragesTemplates={listOfStoragesTemplates}
            editBins={editBins}
            token={token}
            selectedBin={selectedBin}
            isBinSelected={isBinSelected}
            removeSelectedBin={removeSelectedBin}
            allotmentPolicies={allotmentPolicies}
            qualityCriteria={qualityCriteria}
            selectedCriteriaToDelete={selectedCriteriaToDelete}
          />
          {GrainTrackerForm(t)}
        </div>

        <Tooltip
          title={
            isEditingBin || isAddingStorage || storagesList.length === 0
              ? t("Grain_tracker.to_continue")
              : ""
          }
          placement="right"
        >
          <FormControl>
            <Button
              sx={{ textTransform: "none" }}
              onClick={() => managingRedirectUser()}
              variant="contained"
            >
              {t("Grain_tracker.continue-button")}
            </Button>
          </FormControl>
        </Tooltip>
        {locationSelectedIs.EditingStockage ? (
          <Button
            sx={{ textTransform: "none", marginLeft: "10px" }}
            onClick={() => updateBin(navigate)}
            variant="outlined"
          >
            {t("Edit_location.save_and_quite")}
          </Button>
        ) : (
          ""
        )}
        {ShowsMessagesSuccess(
          t,
          editBinAllotment,
          editBins,
          deleteAllotmentPolicies,
          deleteBinSuccess,
        )}
        <ShowsMessagesError t={t} deleteBinError={deleteBinError} />
      </div>
      <ConfirmUnsavedChangesDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmContinue}
        t={t}
        confirmButtonLabel="Grain_tracker.continue-button"
      />
    </>
  );
};
